import { Brand, MenuMetadata, MenuData } from '../types';

const BRANDS_KEY = 'bb_brands';
const MENUS_KEY = 'bb_menus';

const SAMPLE_BRANDS: Brand[] = [
  { id: 'brand-1', name: 'Asiana' },
  { id: 'brand-2', name: 'Sterling Grill' },
];

const SAMPLE_MENUS: MenuMetadata[] = [
  { id: 'menu-1', brandId: 'brand-1', name: 'Main Menu' },
  { id: 'menu-2', brandId: 'brand-1', name: 'Breakfast' },
  { id: 'menu-3', brandId: 'brand-2', name: 'Grill Specials' },
];

const getStorage = () => {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

const readJson = <T,>(key: string, fallback: T): T => {
  const storage = getStorage();
  if (!storage) return fallback;
  try {
    const raw = storage.getItem(key);
    if (!raw) {
      storage.setItem(key, JSON.stringify(fallback));
      return fallback;
    }
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed) || typeof parsed === 'object') return parsed as T;
    return fallback;
  } catch {
    return fallback;
  }
};

const writeJson = (key: string, value: unknown) => {
  const storage = getStorage();
  if (!storage) return;
  try {
    storage.setItem(key, JSON.stringify(value));
  } catch {
    // ignore write errors
  }
};

export const getBrands = async (): Promise<Brand[]> => {
  const data = readJson<Brand[]>(BRANDS_KEY, SAMPLE_BRANDS);
  writeJson(BRANDS_KEY, data);
  return data;
};

export const getMenus = async (brandId: string): Promise<MenuMetadata[]> => {
  const data = readJson<MenuMetadata[]>(MENUS_KEY, SAMPLE_MENUS);
  writeJson(MENUS_KEY, data);
  return data.filter((m) => m.brandId === brandId);
};

export const getMenu = async (id: string): Promise<MenuData | null> => {
  const key = `bb_menu_${id}`;
  const data = readJson<MenuData | null>(key, null);

  // Sync with metadata to ensure logo/name are up to date
  const menus = readJson<MenuMetadata[]>(MENUS_KEY, SAMPLE_MENUS);
  const metadata = menus.find(m => m.id === id);

  if (!data) {
      return {
          title: { en: metadata?.name || 'New Menu' },
          categories: [],
          status: 'live',
          logo: metadata?.logo
      };
  }
  
  // Merge metadata fields that should be the source of truth
  return {
      ...data,
      logo: metadata?.logo || data.logo,
      title: metadata?.name ? { ...data.title, en: metadata.name } : data.title
  };
};

export const saveMenu = async (id: string, data: MenuData) => {
    const key = `bb_menu_${id}`;
    writeJson(key, data);

    // Update metadata
    const menus = readJson<MenuMetadata[]>(MENUS_KEY, SAMPLE_MENUS);
    const index = menus.findIndex(m => m.id === id);
    if (index !== -1) {
        const itemCount = data.categories.reduce((acc, cat) => acc + cat.items.length, 0);
        menus[index] = {
            ...menus[index],
            updatedAt: new Date().toISOString(),
            itemCount,
            status: data.status || menus[index].status
        };
        writeJson(MENUS_KEY, menus);
    }
};

export const createBrand = async (name: string, logo?: string | null) => {
  const brands = readJson<Brand[]>(BRANDS_KEY, SAMPLE_BRANDS);
  const brand: Brand = { id: `brand-${Date.now()}`, name, logo: logo || undefined };
  brands.push(brand);
  writeJson(BRANDS_KEY, brands);
  return brand;
};

export const createMenu = async (brandId: string, name: string, logo?: string | null) => {
  const menus = readJson<MenuMetadata[]>(MENUS_KEY, SAMPLE_MENUS);
  const menu: MenuMetadata = { 
      id: `menu-${Date.now()}`, 
      brandId, 
      name, 
      logo: logo || undefined,
      status: 'offline',
      itemCount: 0,
      updatedAt: new Date().toISOString()
  };
  menus.push(menu);
  writeJson(MENUS_KEY, menus);
  return menu;
};

export const duplicateMenu = async (menuId: string) => {
    const sourceMenu = await getMenu(menuId);
    if (!sourceMenu) return null;

    const menus = readJson<MenuMetadata[]>(MENUS_KEY, SAMPLE_MENUS);
    const sourceMetadata = menus.find(m => m.id === menuId);
    if (!sourceMetadata) return null;

    const newId = `menu-${Date.now()}`;
    const newMetadata: MenuMetadata = {
        ...sourceMetadata,
        id: newId,
        name: `${sourceMetadata.name} (Copy)`,
        updatedAt: new Date().toISOString()
    };

    menus.push(newMetadata);
    writeJson(MENUS_KEY, menus);
    
    // Save the content
    const key = `bb_menu_${newId}`;
    writeJson(key, sourceMenu);
    
    return newMetadata;
};

export const deleteBrand = async (id: string) => {
  const brands = readJson<Brand[]>(BRANDS_KEY, SAMPLE_BRANDS).filter((b) => b.id !== id);
  const menus = readJson<MenuMetadata[]>(MENUS_KEY, SAMPLE_MENUS).filter((m) => m.brandId !== id);
  writeJson(BRANDS_KEY, brands);
  writeJson(MENUS_KEY, menus);
};

export const deleteMenu = async (id: string) => {
  const menus = readJson<MenuMetadata[]>(MENUS_KEY, SAMPLE_MENUS).filter((m) => m.id !== id);
  writeJson(MENUS_KEY, menus);
};

export const uploadImageToR2 = async (file: File) => {
  if (typeof FileReader === 'undefined' || !file) return '';
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
};

export const updateBrand = async (id: string, name: string, logo?: string | null) => {
  const brands = readJson<Brand[]>(BRANDS_KEY, SAMPLE_BRANDS).map((b) =>
    b.id === id ? { ...b, name, logo: logo || undefined } : b
  );
  writeJson(BRANDS_KEY, brands);
};

export const updateMenuMetadata = async (id: string, name: string, logo?: string | null, languages?: string[]) => {
  const menus = readJson<MenuMetadata[]>(MENUS_KEY, SAMPLE_MENUS).map((m) =>
    m.id === id ? { ...m, name, logo: logo || undefined, languages } : m
  );
  writeJson(MENUS_KEY, menus);
};
